<template>
	<AppDialog dialogName="ConvertToSubflowBlockDialog">
		<div class="convert-to-subflow-block-dialog">
			<div class="dialog-title">Convert to subflow</div>
			<div class="dialog-description">
				Subflows allow you to reuse a collection of steps in multiple features. <span class="primary-link-style">Learn more</span>
			</div>
			<SubflowUpgradeRequiredBadge />
			<div class="dialog-body">
				<div class="paragraph-title mt-5">Confirm selection</div>
				<div class="paragraph-description">
					<InfoSection class="pb-2">
						<div class="font-size-xs mb-1 ml-1">
							{{ nodesInSelectedRoute.length }} step{{ nodesInSelectedRoute.length > 1 ? 's' : '' }} selected
						</div>
						<div v-for="nodeId in nodesInSelectedRoute" class="flex-start--center" :key="nodeId">
							<IntegrationIcon :integrationName="combinedNodesData[nodeId].service_slug" :padding="6" />
							<div class="action-title ml-2 font-size-sm">{{ getAction(combinedNodesData[nodeId].action).title }}</div>
						</div>
					</InfoSection>
					<AppCheckbox
						color="red"
						label="I understand that these steps will be moved into a subflow - can't be undone"
						:isOn.sync="confirmConvertSubflow"
					/>
				</div>
				<div class="paragraph-title mt-5">Name subflow</div>
				<AppTextField v-model="subflowName" ref="subflow_name" class="mt-2" placeholder="Enter subflow name" outlined />
			</div>
			<div class="dialog-bottom flex-centerize flex-column">
				<SecondaryButton
					large
					@click="createAndOpenSubflow"
					:disabled="!confirmConvertSubflow || subflowName.length < 2 || loading"
					:loading="loading"
				>
					Convert and open <SIcon name="external-link" />
				</SecondaryButton>
			</div>
		</div>
	</AppDialog>
</template>

<script>
import AppDialog from '@/components/dialogs/AppDialog/AppDialog';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import SubflowUpgradeRequiredBadge from '@/components/dialogs/Subflows/SubflowUpgradeRequiredBadge.vue';
import { getAction } from '@/store/modules/actions/getters';
import { nodesData } from '@/store/modules/selectedWorkflow/getters';
import { convertGraphToSubflow, setIsSelectingWorkflowRoute, setIsRouteSelected } from '@/store/modules/workflowGraphs/actions';
import { nodesInSelectedRoute } from '@/store/modules/workflowGraphs/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';

export default {
	name: 'ConvertToSubflowBlockDialog',
	components: {
		AppDialog,
		SubflowUpgradeRequiredBadge,
	},
	props: {
		dialogPromiseHandlers: { type: Object },
		projectId: { type: Number },
	},
	data() {
		return { subflowName: '', confirmConvertSubflow: false, loading: false, localCopyOfNodesData: null };
	},
	computed: {
		...mapGettersShorthand({ nodesInSelectedRoute, nodesData, getAction }),
		combinedNodesData() {
			return this.loading ? this.localCopyOfNodesData : this.nodesData;
		},
	},
	methods: {
		...mapActionsShorthand({ convertGraphToSubflow, setIsSelectingWorkflowRoute, setIsRouteSelected }),
		close(converted) {
			this.dialogPromiseHandlers.resolve(converted);
			AppDialogManagerService.closeCurrentDialog();
		},
		cancel() {
			this.close(false);
		},
		async createAndOpenSubflow() {
			const { subflowName, projectId } = this;
			this.localCopyOfNodesData = { ...this.nodesData };
			this.loading = true;
			try {
				const { new_workflow_id: newWorkflowId } = await this.convertGraphToSubflow({
					subflowName,
				});

				const routeData = this.$router.resolve({ name: 'Feature', params: { wfID: newWorkflowId, pID: projectId } });
				window.open(routeData.href, '_blank');
				this.setIsSelectingWorkflowRoute(false);
				this.setIsRouteSelected(false);
				this.close(true);
			} catch (res) {
				const message = res.response?.data.error?.msg || res.message;
				if (message) {
					this.$toast.error(`Failed to convert steps. ${message}`);
				} else {
					this.$toast.error(`Failed to convert steps, please try again`);
				}
			} finally {
				this.localCopyOfNodesData = null;
				this.loading = false;
			}
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.$refs.subflow_name.focus();
		});
	},
};
</script>

<style scoped lang="scss">
.convert-to-subflow-block-dialog {
	border-radius: 15px !important;
	box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16) !important;

	::v-deep(.widget-preview) {
		min-height: 350px !important;
	}

	.dialog-body {
		max-height: 400px;
		overflow-y: auto;

		.info-box {
			color: #596ee1;
		}

		.paragraph-title {
			font-size: 16px;
			font-weight: 600;
		}
		.paragraph-description {
			font-size: 14px;
			font-weight: 400;
		}
	}

	.dialog-bottom {
		::v-deep(.v-btn) {
			min-height: 45px !important;
			border-radius: 12px !important;

			svg {
				margin: 4px 0 0 8px !important;

				path {
					stroke: white !important;
				}
			}

			&.v-btn--disabled svg path {
				stroke: #ababab !important;
			}
		}
	}

	::v-deep(.app-checkbox) {
		margin: 0 !important;

		label {
			font-size: 14px;
			color: var(--primary-font-color) !important;
		}

		i::before {
			color: var(--primary-color);
		}
	}

	.action-title {
		margin-top: 2px;
	}
}
</style>
